import './AiChatPanel.css';
import { useAiChatPanelContext } from '../../contexts/AiChatPanelContext';
import useFloatingPanel from './hooks/useFloatingPanel';
import useTeiaHistory from './hooks/useTeiaHistory';
import useCanvasState from './hooks/useCanvasState';
import useScenarioEngine from './hooks/useScenarioEngine';
import useTeiaConversation from './hooks/useTeiaConversation';
import useInsightsDrawer from './hooks/useInsightsDrawer';
import PanelHeader from './components/PanelHeader';
import MessageList from './components/MessageList';
import Composer from './components/Composer';
import InsightsPanel from './components/insights/InsightsPanel';

function AiChatPanel() {
    const { isOpen, close, spawnPosition, setSpawnPosition } = useAiChatPanelContext();
    const { panelRef, collapsed, toggleCollapsed, position, hasCustomPosition, dragging, onHeaderMouseDown, onHeaderClick } = useFloatingPanel();
    const { history, botHistory, selectedEntryId, setSelectedEntryId, selectedEntry, addHistoryEntry, patchHistoryEntry, deleteHistoryEntry, clearBotHistory } = useTeiaHistory();
    const { canvasBlocksRef, canvasCategoriesRef, existingBlockIds } = useCanvasState();
    const engine = useScenarioEngine({ canvasBlocksRef, canvasCategoriesRef });
    const {
        messages, draft, setDraft, isGenerating, lastRun, sessionUsage, pushMessage,
        sendMessage, startNewSession, revertLastRun, replayEntry, removeEntryBlocks, resumeConversation,
    } = useTeiaConversation({ engine, history, addHistoryEntry, patchHistoryEntry, setSelectedEntryId, existingBlockIds });
    const {
        insightsOpen, setInsightsOpen, insightsTab, setInsightsTab, insightsSide, copiedKey, copyToClipboard, goToBlock, toggleInsights, toggleInsightsForEntry,
    } = useInsightsDrawer({ panelRef, position, canvasBlocksRef, selectedEntry, selectedEntryId, setSelectedEntryId, pushMessage });

    return (
        <div
            ref={panelRef}
            className={"ai_chat_panel" + (isOpen ? " open" : "") + (collapsed ? " collapsed" : "") + (hasCustomPosition ? " positioned" : "") + (dragging ? " dragging" : "") + (isGenerating ? " generating" : "")}
            style={hasCustomPosition ? { left: position.left, top: position.top } : undefined}
        >
            <div className="ai_chat_panel_glow" aria-hidden="true"></div>
            <PanelHeader
                collapsed={collapsed}
                isGenerating={isGenerating}
                hasSpawnPosition={Boolean(spawnPosition)}
                onResetSpawn={() => setSpawnPosition(null)}
                insightsOpen={insightsOpen}
                onToggleInsights={toggleInsights}
                hasMessages={messages.length > 0}
                onNewSession={startNewSession}
                onToggleCollapsed={toggleCollapsed}
                onClose={close}
                onMouseDown={onHeaderMouseDown}
                onClick={onHeaderClick}
            />
            <div className="ai_chat_panel_body">
                <div className="ai_chat_panel_body_inner">
                    <MessageList
                        messages={messages}
                        isGenerating={isGenerating}
                        botHistory={botHistory}
                        insightsOpen={insightsOpen}
                        insightsTab={insightsTab}
                        selectedEntry={selectedEntry}
                        onToggleTokens={toggleInsightsForEntry}
                    />
                    <Composer
                        draft={draft}
                        onDraftChange={setDraft}
                        onSend={sendMessage}
                        isGenerating={isGenerating}
                        canRevert={Boolean(lastRun)}
                        onRevert={revertLastRun}
                    />
                </div>
            </div>
            {insightsOpen &&
                <InsightsPanel
                    tab={insightsTab}
                    onTab={setInsightsTab}
                    side={insightsSide}
                    onClose={() => setInsightsOpen(false)}
                    entry={selectedEntry}
                    entries={botHistory}
                    session={sessionUsage}
                    existingBlockIds={existingBlockIds}
                    busy={isGenerating}
                    copiedKey={copiedKey}
                    onCopy={copyToClipboard}
                    onGoToBlock={goToBlock}
                    onSelect={setSelectedEntryId}
                    onViewResponse={(entryId) => { setSelectedEntryId(entryId); setInsightsTab('response'); }}
                    onResume={resumeConversation}
                    onReplay={replayEntry}
                    onRemoveBlocks={removeEntryBlocks}
                    onDeleteEntry={deleteHistoryEntry}
                    onClearHistory={clearBotHistory}
                />
            }
        </div>
    );
}

export default AiChatPanel;
